import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/AuthProvider'
import { CONSENT_TXN_KEY } from '../useConsentReturn'
import { useApproveOAuthMutation, useDenyOAuthMutation, useGetOAuthPendingQuery } from '../mcpApi'
import type { OAuthRedirect } from '../types'
import { ConsentCard } from '../components/ConsentCard'
import { ConsentMessage } from '../components/ConsentMessage'
import { messagesNow, useMessages } from '@/i18n'
import { messages } from '../messages'

// Navigate the whole browser to the Claude callback — never the router — and
// only for http(s) (guards against javascript:/data: from a bad backend).
// Returns whether it actually navigated, so callers can release a busy-lock
// when the redirect_url is declined.
const goExternal = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      window.location.href = parsed.toString()
      return true
    }
    return false
  } catch {
    return false /* invalid URL — do not navigate */
  }
}

const statusOf = (error: unknown): number | undefined =>
  typeof error === 'object' && error !== null && 'status' in error
    ? (error as { status?: number }).status
    : undefined

// Terminal (non-recoverable-without-retry) states for the pending-request fetch:
// expired txn, wrong account, or any other load failure. Extracted to keep the
// component's cyclomatic complexity within the eslint budget.
const pendingTerminalScreen = (params: {
  expired: boolean
  status: number | undefined
  error: unknown
  data: unknown
}) => {
  const m = messagesNow(messages)
  if (params.expired || params.status === 404) {
    return <ConsentMessage title={m.requestExpired} hint={m.requestExpiredHint} />
  }
  if (params.status === 403) return <ConsentMessage title={m.authorRequired} />
  if (params.error || !params.data) {
    return <ConsentMessage title={m.requestLoadFailed} hint={m.tryAgain} />
  }
  return null
}

export const OAuthConsent = () => {
  const [params] = useSearchParams()
  const m = useMessages(messages)
  const txn = params.get('txn')
  const { isAuthorized, isAuthorizeLoading } = useAuth()
  const [busy, setBusy] = useState(false)
  const [expired, setExpired] = useState(false)
  const pending = useGetOAuthPendingQuery(txn ?? '', { skip: !txn || !isAuthorized })
  const [approve] = useApproveOAuthMutation()
  const [deny] = useDenyOAuthMutation()

  const needsLogin = Boolean(txn) && !isAuthorized && !isAuthorizeLoading

  // Login gate: stash txn + send to the existing login; ConsentReturnGate
  // resumes here after the round-trip.
  useEffect(() => {
    if (!needsLogin || !txn) return
    sessionStorage.setItem(CONSENT_TXN_KEY, txn)
    window.location.href = '/auth'
  }, [needsLogin, txn])

  // Dev-only manual view (no MCP server yet): /oauth/consent?txn=mock. Remove at
  // Phase 5 (search `txn === 'mock'`). After the hooks → no rules-of-hooks issue.
  if (import.meta.env.DEV && txn === 'mock') {
    return (
      <ConsentCard
        pending={{ client_id: 'mock', client_name: 'Claude', scopes: [] }}
        busy={false}
        onApprove={() => window.alert('mock: approved')}
        onDeny={() => window.alert('mock: denied')}
      />
    )
  }

  if (!txn) return <ConsentMessage title={m.badLink} />
  if (needsLogin) return <ConsentMessage title={m.loginRequired} />
  if (isAuthorizeLoading || pending.isLoading) return <ConsentMessage title={m.loading} />

  const terminal = pendingTerminalScreen({
    expired,
    status: statusOf(pending.error),
    error: pending.error,
    data: pending.data,
  })
  if (terminal) return terminal
  if (!pending.data) return null // unreachable: pendingTerminalScreen already covers !data

  const run = (mutate: () => { unwrap: () => Promise<OAuthRedirect> }) => async () => {
    setBusy(true)
    try {
      const { redirect_url } = await mutate().unwrap()
      if (!goExternal(redirect_url)) setBusy(false)
    } catch (err) {
      if (statusOf(err) === 404) {
        setExpired(true)
        return
      }
      toast.error(m.actionFailed)
      setBusy(false)
    }
  }

  return (
    <ConsentCard
      pending={pending.data}
      busy={busy}
      onApprove={run(() => approve({ txn }))}
      onDeny={run(() => deny({ txn }))}
    />
  )
}
